import React, { useCallback } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import { toTitle } from "@/utils/case";
import { Company } from "@/features/types";
import { t } from "i18next";
import { dashboardFavoriteBrandApi } from "@/features/dashboard/features/favorite-brand-section/api/api";
import { useRecoilState } from "recoil";
import {
  favoriteBrandSectionFavoriteBrandStore,
  favoriteBrandSectionLineSheetPageStore,
  favoriteBrandSectionLineSheetStore,
} from "@/features/dashboard/features/favorite-brand-section/store/favorite-brand-section-store";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function FavoriteBrandModal({ isOpen, onClose }: Props) {
  const { tTitle, tSentence } = useI18nHelper();
  const identity = useIdentity();
  const [selectBrandAvailable, setSelectBrandAvailable] =
    React.useState<boolean>(true);

  const [favoriteBrandLineSheets, setFavoriteBrandLineSheets] = useRecoilState(
    favoriteBrandSectionLineSheetStore
  );
  const [
    favoriteBrandSectionFavoriteBrands,
    setFavoriteBrandSectionFavoriteBrands,
  ] = useRecoilState(favoriteBrandSectionFavoriteBrandStore);
  const [
    favoriteBrandSectionLineSheetPage,
    setFavoriteBrandSectionLineSheetPageStore,
  ] = useRecoilState(favoriteBrandSectionLineSheetPageStore);

  const [isLoading, setIsLoading] = React.useState(false);

  const handleClose = () => {
    if (favoriteBrandSectionFavoriteBrands) {
      setTmpBrands(favoriteBrandSectionFavoriteBrands);
    }
    onClose();
  };

  const [tmpBrands, setTmpBrands] = React.useState<Company[]>([]);

  React.useEffect(() => {
    if (favoriteBrandSectionFavoriteBrands) {
      setTmpBrands(favoriteBrandSectionFavoriteBrands);
    }
  }, [favoriteBrandSectionFavoriteBrands]);

  React.useEffect(() => {
    if (tmpBrands.length < 10) {
      setSelectBrandAvailable(true);
    }
  }, [tmpBrands]);

  const handleSelect = useCallback((value?: Company[]) => {
    if (value) {
      if (value.length == 10) {
        setTmpBrands(value);
        setSelectBrandAvailable(false);
        return;
      } else {
        setTmpBrands(value);
      }
    } else {
      setTmpBrands([]);
    }
  }, []);

  const queryClient = useQueryClient();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"}>
        <ModalHeader background={"#F7F9FA"} padding={"12px 16px"}>
          <Text textStyle={"body-md-bold"} color={"#444444"}>
            {tTitle("my_favorite_brand_modal_title")}
          </Text>
        </ModalHeader>
        <ModalBody display="flex" flexDirection={"column"} gap={"24px"}>
          <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
            {tSentence("my_favorite_brand_modal_description")}
          </Text>
          <FormControl>
            <FormLabel color={"#6F6F6C"}>
              <Text textStyle={"body-sm-regular"}>
                {tTitle("request_markup_info_modal_label")}
              </Text>
            </FormLabel>
            <AppCompanySelect
              key={"AgencyLineSheetSetListFilter_Brand"}
              name={toTitle(t("common:brand"))}
              types={["BRAND"]}
              isMultiple={true}
              id={tmpBrands.map((item) => item.id)}
              width={"100%"}
              onSelect={handleSelect}
              isClearable={false}
              // setOuterOpen={setOuterOpen}
              // outerOpen={outerOpen}
              customText={tTitle("brand_add")}
              isDisabled={!selectBrandAvailable}
            />
          </FormControl>
          {tmpBrands.length > 0 && (
            <Flex
              gap={"12px"}
              flexWrap={"wrap"}
              border={"1px solid #E4E4E4"}
              borderRadius={"4px"}
              padding={"12px"}
            >
              {tmpBrands.slice().sort((a, b) => a.name.localeCompare(b.name)).map((item) => {
                return (
                  <Flex
                    key={item.id}
                    background={"#F7F9FA"}
                    borderRadius={"4px"}
                    padding={"4px 8px"}
                    gap={"8px"}
                    alignItems={"center"}
                  >
                    <Text textStyle={"body-sm-bold"} color={"#444444"}>
                      {item.name}
                    </Text>
                    <SVG
                      src={"/icons-v1_1/x.svg"}
                      width={"16px"}
                      height={"16px"}
                      onClick={() => {
                        setTmpBrands((prev) =>
                          prev.filter((brand) => brand.id !== item.id)
                        );
                      }}
                      cursor={"pointer"}
                    />
                  </Flex>
                );
              })}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter
          display={"flex"}
          justifyContent={"space-between"}
          gap={"12px"}
          paddingLeft={"16px"}
        >
          <AppButton
            key={"Brand_ResetFilter"}
            variant={"ghost"}
            size={"medium"}
            onClick={() => {
              setTmpBrands([]);
            }}
            width={"72px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {t("clear_all")}
              </Text>
            </Flex>
          </AppButton>
          <Box display="flex" gap="12px">
            <AppButton variant={"normal"} onClick={handleClose}>
              <Text textStyle={"body-sm-bold"} color={"#444444"}>
                {tTitle("cancel")}
              </Text>
            </AppButton>
            <AppButton
              isLoading={isLoading}
              variant={"primary"}
              onClick={async () => {
                if (!identity) {
                  handleClose();
                  return;
                }
                try {
                  setIsLoading(true);
                  await dashboardFavoriteBrandApi.saveFavoriteBrand(
                    identity.company?.id!,
                    tmpBrands.map((item) => item.id)
                  );
                  const response =
                    await dashboardFavoriteBrandApi.getFavoriteBrandLineSheetList(
                      identity.company?.id!,
                      "SEASON"
                    );

                  setFavoriteBrandLineSheets((prev) => ({
                    ...prev,
                    ["SEASON"]: response.data.line_sheets,
                  }));
                  const response2 =
                    await dashboardFavoriteBrandApi.getFavoriteBrandLineSheetList(
                      identity.company?.id!,
                      "STOCK"
                    );

                  setFavoriteBrandLineSheets((prev) => ({
                    ...prev,
                    ["STOCK"]: response2.data.line_sheets,
                  }));
                  setFavoriteBrandSectionFavoriteBrands(tmpBrands);
                  setFavoriteBrandSectionLineSheetPageStore({
                    ["STOCK"]: {
                      page: 1,
                      hasMore: true,
                    },
                    ["SEASON"]: {
                      page: 1,
                      hasMore: true,
                    },
                  });
                  setIsLoading(false);

                  queryClient.invalidateQueries({
                    queryKey: ["api-order", "brands"],
                  });

                  handleClose();
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              <Text textStyle={"body-sm-bold"} color={"#FFFFFF"}>
                {tTitle("common:save")}
              </Text>
            </AppButton>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
